/**
 * Billing Page Facade (100% Shared)
 * 
 * Composes all necessary facades for the billing page.
 * Uses dependency injection pattern to remain platform-agnostic.
 * 
 * This facade is 100% shared between web and plugin.
 * Platform-specific implementations (navigation, auth) are injected.
 * 
 * Pattern: Clean composition - delegates to smaller facades, no inline business logic.
 * Reference: useMyProfilePageFacade
 * 
 * Usage (Web):
 * ```tsx
 * const navigation = useWebNavigation();
 * const auth = useAuthDialog();
 * const facade = useBillingPageFacade({ 
 *   navigation, 
 *   auth,
 *   showToast 
 * });
 * ```
 * 
 * Usage (Plugin):
 * ```tsx
 * const navigation = usePluginNavigation();
 * const auth = usePairingCodeAuth();
 * const facade = useBillingPageFacade({ 
 *   navigation, 
 *   auth,
 *   showToast 
 * });
 * ```
 * 
 * @module useBillingPageFacade
 * @category Facades/Pages
 */

import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useAppSelector } from '../../../store/hooks';
import { selectUser } from '../../../store/slices/appSlice';
import { useHeaderConfig } from '../../useHeaderConfig';
import { useSidebarConfig, type SidebarConfig } from '../shared/useSidebarConfig';
import { useSubscriptionExpiration, type SubscriptionExpirationState } from '../shared/useSubscriptionExpiration';
import type { NavigationFacade, ShowToastFn, AuthFacade } from '../shared/types';
import { apiClient } from '../../../api/client';
import { ApiUrls } from '../../../api/ApiUrls';

export interface BillingPageFacadeParams {
  /**
   * Platform-specific navigation implementation
   */
  navigation: NavigationFacade;

  /**
   * Platform-specific auth implementation
   */
  auth: AuthFacade;

  /**
   * Platform-specific toast notification function
   */
  showToast: ShowToastFn;

  /**
   * Optional: Function to refresh user profile data after payment
   * Used after successful Stripe payment to update plan details
   */
  onRefreshUserProfile?: () => Promise<void>;

  /**
   * Optional: Check if payment was successful (from URL params)
   * Used to show success toast and refresh data after Stripe redirect
   */
  paymentSuccess?: boolean;
}

/**
 * Invoice interface for billing history
 * Matches API response from GET /payments/invoices
 */
export interface Invoice {
  id: string;
  status: string; // Raw API status: 'paid', 'open', 'void', 'uncollectible', etc.
  amountDue: number;
  amountPaid: number;
  currency: string; // e.g., 'usd'
  dueDate: string; // ISO date string
  invoicePdf?: string; // PDF URL
  hostedInvoiceUrl?: string; // Stripe hosted invoice URL for viewing/downloading
  // Display fields (computed from API data)
  date?: string; // Formatted date for display
  amount?: string; // Formatted amount for display
  displayStatus?: 'paid' | 'pending' | 'failed'; // Mapped status for UI display
}

/**
 * Payment method interface
 */
export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  maskedNumber: string;
  expMonth: number;
  expYear: number;
  expiryDate: string;
}

export interface BillingPageFacade {
  // ========== State ==========
  state: {
    isLoggedIn: boolean;
    user: any | null;
    hasUpgradedPlan: boolean; // New: indicates if user has a paid plan
  };

  // ========== Header ==========
  header: ReturnType<typeof useHeaderConfig>;

  // ========== Navigation ==========
  navigation: NavigationFacade;

  // ========== Auth ==========
  auth: AuthFacade;

  // ========== Sidebar ==========
  sidebar: SidebarConfig;

  // ========== Billing Actions ==========
  actions: {
    onUpgrade: (planId: string) => void;
    onConfirmUpgrade: () => Promise<void>;
    onCancelUpgrade: () => void;
    onDowngrade: () => void;
    onConfirmDowngrade: () => Promise<void>;
    onCancelDowngrade: () => void;
    onCancelPendingDowngrade: () => void;
    onConfirmCancelPendingDowngrade: () => Promise<void>;
    onCloseCancelPendingDowngradeModal: () => void;
    onRenewSubscription: () => void;
    onDownloadInvoice: (invoiceId: string) => Promise<void>;
    onUpdatePaymentMethod: () => Promise<void>;
  };

  // ========== Subscription Expiration ==========
  subscriptionExpiration: SubscriptionExpirationState;

  // ========== Upgrade Dialog State ==========
  upgradeDialog: {
    isOpen: boolean;
    isLoading: boolean;
  };

  // ========== Downgrade Dialog State ==========
  downgradeDialog: {
    isOpen: boolean;
    isLoading: boolean;
  };

  // ========== Pending Downgrade State ==========
  pendingDowngrade: {
    isPending: boolean;
    effectiveDate: string | null;
    isCancelling: boolean;
  };

  // ========== Cancel Pending Downgrade Modal State ==========
  cancelPendingDowngradeModal: {
    isOpen: boolean;
    isLoading: boolean;
  };

  // ========== Credit Exhaustion Modal State ==========
  creditExhaustionModal: {
    isOpen: boolean;
    onClose: () => void;
    availableCredits: number;
    totalCredits: number;
  };

  // ========== Data ==========
  data: {
    currentPlan: {
      name: string;
      price: number;
      billingPeriod: string;
    };
    creditBalances: Array<{
      name: string;
      used: number;
      total: number;
      percentage: number;
    }>;
    monthlyCredits: number;
    purchasedCredits: number;
    nextResetDate: string;
    availablePlans: Array<{
      id: string;
      checkoutId: string; // periodicPlans[0].id - used for Stripe checkout
      name: string;
      price: number;
      billingPeriod: string;
      currency: string;
      description: string;
      subDescription: string;
      isCurrent: boolean;
      recommended?: boolean;
      features: Array<{
        id: string;
        name: string;
        included: boolean;
      }>;
    }>;
    invoices: Invoice[];
    isLoadingPlans: boolean;
    isLoadingInvoices: boolean;
    paymentMethod: PaymentMethod | null;
    isLoadingPaymentMethod: boolean;
    billingCycle: {
      billingPeriod: string; // e.g., "Monthly"
      nextBillingDate: string; // Formatted date, e.g., "February 21, 2026"
      nextCharge: string; // Formatted amount, e.g., "$19.00"
    } | null;
  };
}

/**
 * Billing Page Facade Hook
 * 
 * Composes all necessary facades for the billing page.
 * 100% shared between web and plugin through dependency injection.
 */
export function useBillingPageFacade({
  navigation,
  auth,
  showToast,
  onRefreshUserProfile,
  paymentSuccess,
}: BillingPageFacadeParams): BillingPageFacade {
  // ========== State ==========
  const user = useAppSelector(selectUser);
  // Read planDetails directly from Redux state (same pattern as useCreditLimitBanner)
  const planDetails = useAppSelector((state: any) => state.app.planDetails);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [downgradeDialogOpen, setDowngradeDialogOpen] = useState(false);
  const [isDowngrading, setIsDowngrading] = useState(false);
  const [isCancellingDowngrade, setIsCancellingDowngrade] = useState(false);
  const [cancelPendingDowngradeModalOpen, setCancelPendingDowngradeModalOpen] = useState(false);
  // Derive hasUpgradedPlan from actual plan data
  // Check if user has a paid plan (not Free)
  const currentPlanName = planDetails?.name || 'Free';
  const hasUpgradedPlanFromData = currentPlanName !== 'Free';
  const [localUpgradeFlag, setLocalUpgradeFlag] = useState(false); // Local flag for immediate UI update
  // Combine: show upgraded UI if either actual plan is paid OR local flag is set (for immediate UI update after payment)
  const hasUpgradedPlan = hasUpgradedPlanFromData || localUpgradeFlag;
  const [creditExhaustionModalOpen, setCreditExhaustionModalOpen] = useState(false);
  const hasShownModalRef = useRef(false); // Track if we've already shown the modal for current billing data
  const [availablePlans, setAvailablePlans] = useState<Array<{
    id: string;
    checkoutId: string; // periodicPlans[0].id - used for Stripe checkout
    name: string;
    price: number;
    billingPeriod: string;
    currency: string;
    description: string;
    subDescription: string;
    isCurrent: boolean;
    recommended?: boolean;
    features: Array<{ id: string; name: string; included: boolean }>;
  }>>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [isLoadingPaymentMethod, setIsLoadingPaymentMethod] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  
  // Refs to prevent double fetching (React Strict Mode)
  const hasFetchedPlansRef = useRef(false);
  const hasFetchedPaymentMethodRef = useRef(false);
  const hasFetchedInvoicesRef = useRef(false);

  // ========== Header Config ==========
  const headerConfig = useHeaderConfig({
    onNavigateToProfile: navigation.toProfile,
    onNavigateToBilling: () => {}, // Already on billing page
    onNavigateToReport: navigation.toReportProblem,
    onLoginClick: auth.openLogin,
    onRegisterClick: auth.openRegister,
    showToast,
    currentPath: '/billing',
  });

  // ========== Sidebar Config ==========
  const sidebar = useSidebarConfig({
    pageType: 'standard',
    isLoggedIn: headerConfig.isLoggedIn,
    onResetConversation: () => {}, // No conversation reset on billing page
    onNavigateToLanding: navigation.toLanding,
    onNavigateToProjectsList: navigation.toProjectsList,
    currentPath: '/billing',
    onModeChange: () => {},
  });

  // Get plan name from planDetails (needed for plans fetching)
  const planName = planDetails?.name || 'Free';

  // Default plans fallback function
  const getDefaultPlans = useCallback((currentPlanName: string) => {
    return [
      {
        id: 'free',
        checkoutId: 'free', // Not used for free plan
        name: 'Free',
        price: 0,
        billingPeriod: 'month',
        currency: 'USD',
        description: 'Discover the core power of Ideoz',
        subDescription: 'Quickly frame design problems and generate a prototype proof to validate your initial direction',
        isCurrent: currentPlanName === 'Free',
        features: [
          { id: 'f0', name: '40 credits/month', included: true },
          { id: 'f1', name: '1 live project', included: true },
          { id: 'f2', name: 'Up to 10 file uploads', included: true },
          // { id: 'f3', name: 'Single, non-iterative prototype', included: true },
          { id: 'f4', name: 'Rich context building', included: true },
          { id: 'f5', name: 'Enrich context with Ideoz canvas', included: true },
          { id: 'f6', name: 'Google drive integration', included: true },
          { id: 'f7', name: 'Light theme for your prototype', included: true },
        ],
      },
      {
        id: 'smart',
        checkoutId: 'smart', // Will be replaced by API periodicPlans[0].id
        name: 'Smart',
        price: 19,
        billingPeriod: 'month',
        currency: 'USD',
        description: 'Unleash your smarter design workflow',
        subDescription: 'Unlimited projects, rich context, and flexible prototypes for confident, faster results ',
        isCurrent: currentPlanName === 'Smart',
        recommended: true,
        features: [
          { id: 'f0', name: '250 credits/month', included: true },
          { id: 'f1', name: 'Unlimited live projects', included: true },
          { id: 'f2', name: 'Unlimited file uploads', included: true },
          // { id: 'f1', name: 'Unlimited prototypes', included: true },
          { id: 'f2', name: 'Multiple prototypes with iterative flows', included: true },
          { id: 'f3', name: 'Update prototype design system', included: true },
          { id: 'f4', name: 'Advanced context builder for richer designs', included: true },
          { id: 'f5', name: 'Google drive integration', included: true },
          
        ],
      },
    ];
  }, []);

  // ========== Fetch Plans from API ==========
  // Fetch plans once when entering the page (not conditional)
  useEffect(() => {
    if (hasFetchedPlansRef.current) return;
    hasFetchedPlansRef.current = true;
    
    const fetchPlans = async () => {
      setIsLoadingPlans(true);
      try {
        const response = await apiClient.get(ApiUrls.getPlans);
        // Map API response to expected format
        // API returns: [{ id, name, description, active, periodicPlans: [{ id, amount, resetCycle, currency }] }]
        const plansData = response.data?.data || response.data || [];
        // Get default plans for lookups (features)
        const defaultPlansLookup = getDefaultPlans('');
        
        const mappedPlans = Array.isArray(plansData) ? plansData.map((plan: any) => {
          const planId = plan.id || '';
          const planNameFromApi = plan.name || '';
          
          // Get the first active periodic plan for pricing info
          const periodicPlan = plan.periodicPlans?.find((pp: any) => pp.active) || plan.periodicPlans?.[0];
          
          // Extract pricing from periodicPlans
          const checkoutId = periodicPlan?.id || planId; // Use periodicPlan.id for checkout
          const price = periodicPlan?.amount ?? (planNameFromApi === 'Free' ? 0 : planNameFromApi === 'Smart' ? 19 : 0);
          const billingPeriod = periodicPlan?.resetCycle === 'monthly' ? 'month' : 
                               periodicPlan?.resetCycle === 'yearly' ? 'year' : 
                               periodicPlan?.resetCycle || 'month';
          const currency = periodicPlan?.currency || 'USD';
          
          // Get default plan data for features
          const defaultPlanData = defaultPlansLookup.find(p => p.name === planNameFromApi);
          
          return {
            id: planId, // Main plan UUID
            checkoutId, // periodicPlans[0].id - used for Stripe checkout
            name: planNameFromApi,
            price,
            billingPeriod,
            currency,
            description: plan.description || defaultPlanData?.description || '',
            subDescription: defaultPlanData?.subDescription || '',
            isCurrent: planNameFromApi === planName,
            recommended: planNameFromApi === 'Smart',
            features: defaultPlanData?.features || [],
          };
        }) : [];
        
        // If no plans returned or empty, use default plans as fallback
        if (mappedPlans.length === 0) {
          setAvailablePlans(getDefaultPlans(planName));
        } else {
          // Ensure isCurrent is set correctly based on current plan name
          const plansWithCurrent = mappedPlans.map(plan => ({
            ...plan,
            isCurrent: plan.name === planName,
          }));
          setAvailablePlans(plansWithCurrent);
        }
      } catch (error) {
        console.warn('Failed to fetch plans from API, using default plans:', error);
        // Fallback to default plans on error
        setAvailablePlans(getDefaultPlans(planName));
      } finally {
        setIsLoadingPlans(false);
      }
    };

    fetchPlans();
  }, []); // Fetch once on mount, not conditional

  // Update isCurrent flag when planName changes (after plans are fetched)
  useEffect(() => {
    setAvailablePlans(prevPlans => {
      if (prevPlans.length === 0) return prevPlans;
      return prevPlans.map(plan => ({
        ...plan,
        isCurrent: plan.name === planName,
      }));
    });
  }, [planName]);

  // ========== Fetch Payment Methods from API ==========
  // Fetch payment methods automatically when billing page opens
  useEffect(() => {
    if (hasFetchedPaymentMethodRef.current) return;
    hasFetchedPaymentMethodRef.current = true;
    
    const fetchPaymentMethods = async () => {
      setIsLoadingPaymentMethod(true);
      try {
        const response = await apiClient.get(ApiUrls.getPaymentMethods);
        const paymentMethodData = response.data?.paymentMethod || response.data?.data?.paymentMethod || null;
        setPaymentMethod(paymentMethodData);
      } catch (error) {
        console.warn('Failed to fetch payment methods:', error);
        setPaymentMethod(null);
      } finally {
        setIsLoadingPaymentMethod(false);
      }
    };

    fetchPaymentMethods();
  }, []);

  // ========== Fetch Invoices from API ==========
  // Fetch invoices automatically when billing page opens (not conditional)
  useEffect(() => {
    if (hasFetchedInvoicesRef.current) return;
    hasFetchedInvoicesRef.current = true;
    
    const fetchInvoices = async () => {
      setIsLoadingInvoices(true);
      try {
        const response = await apiClient.get(ApiUrls.getInvoices);
        // API returns { invoices: [...] }
        const invoicesData = response.data?.invoices || response.data?.data || response.data || [];
        
        // Map API response to display format
        const mappedInvoices: Invoice[] = Array.isArray(invoicesData) ? invoicesData.map((invoice: any) => {
          // Format date - use dueDate if available, otherwise use current date as fallback
          // Note: API may not provide dueDate, so we'll format it if present
          let formattedDate = 'N/A';
          if (invoice.dueDate) {
            const dueDate = new Date(invoice.dueDate);
            formattedDate = dueDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            });
          } else if (invoice.created || invoice.createdAt) {
            // Fallback to created date if available
            const createdDate = new Date(invoice.created || invoice.createdAt);
            formattedDate = createdDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            });
          }

          // Convert amount from cents to dollars (amounts are in cents: 1900 = $19.00)
          // Use amountPaid if available and > 0, otherwise use amountDue
          const amountInCents = invoice.amountPaid > 0 ? invoice.amountPaid : invoice.amountDue;
          const amountInDollars = amountInCents / 100;
          const currencySymbol = invoice.currency === 'usd' ? '$' : invoice.currency?.toUpperCase() || '$';
          const formattedAmount = `${currencySymbol}${amountInDollars.toFixed(2)}`;

          // Map status: 'paid' -> 'paid', 'open' -> 'pending', others -> 'failed'
          let displayStatus: 'paid' | 'pending' | 'failed' = 'paid';
          if (invoice.status === 'paid') {
            displayStatus = 'paid';
          } else if (invoice.status === 'open' || invoice.status === 'draft') {
            displayStatus = 'pending';
          } else {
            displayStatus = 'failed';
          }

          return {
            id: invoice.id,
            status: invoice.status,
            amountDue: invoice.amountDue || 0,
            amountPaid: invoice.amountPaid || 0,
            currency: invoice.currency || 'usd',
            dueDate: invoice.dueDate || '',
            invoicePdf: invoice.invoicePdf,
            hostedInvoiceUrl: invoice.hostedInvoiceUrl,
            // Display fields
            date: formattedDate,
            amount: formattedAmount,
            displayStatus,
          };
        }) : [];
        setInvoices(mappedInvoices);
      } catch (error) {
        console.warn('Failed to fetch invoices:', error);
        setInvoices([]);
      } finally {
        setIsLoadingInvoices(false);
      }
    };

    fetchInvoices();
  }, []); // Fetch once on mount, not conditional

  // ========== Billing Actions ==========
  const handleUpgrade = useCallback(
    (planId: string) => {
      // Find the plan to get its checkoutId
      const plan = availablePlans.find(p => p.id === planId);
      
      if (!plan) {
        showToast({
          message: 'Plan not found',
          severity: 'error',
        });
        return;
      }
      
      if (plan.name === 'Free' || planId === 'free') {
        showToast({
          message: 'You are already on the Free plan',
          severity: 'info',
        });
        return;
      }

      // Open upgrade confirmation dialog
      // Store the checkoutId (periodicPlans[0].id) for Stripe checkout
      setSelectedPlanId(plan.checkoutId);
      setUpgradeDialogOpen(true);
    },
    [showToast, availablePlans]
  );

  const handleConfirmUpgrade = useCallback(async () => {
    // If no plan is selected, automatically select the Smart plan
    // Use checkoutId (periodicPlans[0].id) for Stripe checkout, not the plan id
    let planIdToUse = selectedPlanId;
    if (!planIdToUse) {
      const smartPlan = availablePlans.find(p => p.name === 'Smart' && !p.isCurrent);
      if (smartPlan) {
        // Use checkoutId which is periodicPlans[0].id for Stripe checkout
        planIdToUse = smartPlan.checkoutId;
        setSelectedPlanId(smartPlan.checkoutId);
      } else {
        showToast({
          message: 'No upgrade plan available',
          severity: 'error',
        });
        return;
      }
    }

    setIsUpgrading(true);
    try {
      // Build success and cancel URLs for Stripe redirect
      const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
      const successUrl = `${baseUrl}/billing?payment=success`;
      const cancelUrl = `${baseUrl}/billing?payment=cancelled`;

      // Build the API URL
      const checkoutApiUrl = ApiUrls.getCheckoutUrl(planIdToUse, successUrl, cancelUrl);
      
      // Show loading message
      showToast({
        message: 'Preparing checkout...',
        severity: 'info',
      });

      // Call the backend API to get the Stripe checkout URL
      // The API returns 303 redirect, so we need to handle it properly
      const response = await apiClient.get(checkoutApiUrl, {
        maxRedirects: 0, // Don't follow redirects automatically
        validateStatus: (status) => status >= 200 && status < 400, // Accept 303 as valid
      });

      // Get the Stripe checkout URL from the response
      // It could be in the Location header (for 303) or in response data
      let stripeCheckoutUrl: string | null = null;
      stripeCheckoutUrl = response.data.url;
     

      if (!stripeCheckoutUrl) {
        throw new Error('No checkout URL received from server');
      }

      // Close the dialog before redirect
      setUpgradeDialogOpen(false);

      // Show redirect message
      showToast({
        message: 'Redirecting to Stripe checkout...',
        severity: 'info',
      });

      // Small delay to show the toast, then redirect to Stripe
      await new Promise(resolve => setTimeout(resolve, 300));

      // Redirect to Stripe checkout page
      window.location.href = stripeCheckoutUrl;
      
    } catch (error: any) {
      console.error('Error initiating upgrade:', error);
      
      // Check if the error response contains a redirect URL (some axios configs might throw on 3xx)
      if (error.response?.headers?.location) {
        const redirectUrl = error.response.headers.location;
        setUpgradeDialogOpen(false);
        showToast({
          message: 'Redirecting to Stripe checkout...',
          severity: 'info',
        });
        await new Promise(resolve => setTimeout(resolve, 300));
        window.location.href = redirectUrl;
        return;
      }
      
      showToast({
        message: error.message || 'Failed to start upgrade process. Please try again.',
        severity: 'error',
      });
      setIsUpgrading(false);
    }
  }, [selectedPlanId, availablePlans, showToast, setSelectedPlanId]);

  const handleCancelUpgrade = useCallback(() => {
    setUpgradeDialogOpen(false);
    setSelectedPlanId(null);
    setIsUpgrading(false);
  }, []);

  // ========== Downgrade Handlers ==========
  const handleDowngrade = useCallback(() => {
    // Only allow downgrade from Smart plan
    if (currentPlanName !== 'Smart') {
      showToast({
        message: 'You can only downgrade from the Smart plan.',
        severity: 'info',
      });
      return;
    }
    setDowngradeDialogOpen(true);
  }, [currentPlanName, showToast]);

  const handleConfirmDowngrade = useCallback(async () => {
    setIsDowngrading(true);
    try {
      // Call the downgrade API
      await apiClient.post(ApiUrls.downgrade);
      
      // Close the dialog
      setDowngradeDialogOpen(false);
      
      // Show success toast
      showToast({
        message: 'Downgrade to free plan has been done successfully.',
        severity: 'success',
      });
      
      // Refresh user profile to get updated plan details (including nextPeriodicPlan)
      if (onRefreshUserProfile) {
        await onRefreshUserProfile();
      }
    } catch (error: any) {
      console.error('Error processing downgrade:', error);
      showToast({
        message: error.response?.data?.message || error.message || 'Failed to process downgrade. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsDowngrading(false);
    }
  }, [showToast, onRefreshUserProfile]);

  const handleCancelDowngrade = useCallback(() => {
    setDowngradeDialogOpen(false);
    setIsDowngrading(false);
  }, []);

  const handleCancelPendingDowngrade = useCallback(() => {
    // Open the confirmation modal instead of directly cancelling
    setCancelPendingDowngradeModalOpen(true);
  }, []);

  const handleConfirmCancelPendingDowngrade = useCallback(async () => {
    // Actually cancel the downgrade after confirmation
    setIsCancellingDowngrade(true);
    try {
      // Call the cancel downgrade API
      await apiClient.post(ApiUrls.cancelDowngrade);
      
      // Close the modal
      setCancelPendingDowngradeModalOpen(false);
      
      // Show success toast
      showToast({
        message: 'Downgrade has been cancelled successfully.',
        severity: 'success',
      });
      
      // Refresh user profile to get updated plan details
      if (onRefreshUserProfile) {
        await onRefreshUserProfile();
      }
    } catch (error: any) {
      console.error('Error cancelling downgrade:', error);
      showToast({
        message: error.response?.data?.message || error.message || 'Failed to cancel downgrade. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsCancellingDowngrade(false);
    }
  }, [showToast, onRefreshUserProfile]);

  const handleCloseCancelPendingDowngradeModal = useCallback(() => {
    setCancelPendingDowngradeModalOpen(false);
  }, []);


  const handleRenewSubscription = useCallback(() => {
    // TODO: Implement actual Stripe renewal flow
    // For now, show toast notification
    showToast({
      message: 'Redirecting to Stripe checkout...',
      severity: 'info',
    });

    // TODO: Replace with actual Stripe checkout URL
    // window.location.href = stripeCheckoutUrl;
  }, [showToast]);

  const handleDownloadInvoice = useCallback(async (invoiceId: string) => {
    try {
      // Find the invoice in the list to get its hostedInvoiceUrl
      const invoice = invoices.find((inv) => inv.id === invoiceId);
      
      if (invoice?.hostedInvoiceUrl) {
        // Redirect to Stripe hosted invoice URL
        window.open(invoice.hostedInvoiceUrl, '_blank');
      } else {
        showToast({
          message: 'Invoice URL not available. Please try again later.',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Failed to open invoice:', error);
      showToast({
        message: 'Failed to open invoice. Please try again.',
        severity: 'error',
      });
    }
  }, [invoices, showToast]);

  const handleUpdatePaymentMethod = useCallback(async () => {
    try {
      showToast({
        message: 'Fetching payment update link...',
        severity: 'info',
      });

      // Build return URL to redirect back to billing page after payment method update
      const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
      const returnUrl = `${baseUrl}/billing`;

      const paymentMethodApiUrl = ApiUrls.getPaymentMethodsUrl(returnUrl);

      // Call the backend API to get the Stripe payment method update URL
      // The API returns 303 redirect, so we need to handle it properly
      const response = await apiClient.get(paymentMethodApiUrl, {
        maxRedirects: 0, // Don't follow redirects automatically
        validateStatus: (status) => status >= 200 && status < 400, // Accept 303 as valid
      });

      // Get the Stripe payment method update URL from the response
      // It could be in the Location header (for 303) or in response data
      let paymentMethodUrl: string | null = null;
      
      paymentMethodUrl = response.data.url;

      if (!paymentMethodUrl) {
        throw new Error('No payment method update URL received from server');
      }

      // Redirect to Stripe billing portal in the same window
      window.location.href = paymentMethodUrl;
    } catch (error: any) {
      console.error('Failed to fetch payment method URL:', error);
      
      // Check if the error response contains a redirect URL (some axios configs might throw on 3xx)
      if (error.response?.headers?.location || error.response?.headers?.Location) {
        const redirectUrl = error.response.headers.location || error.response.headers.Location;
        // Redirect in the same window
        window.location.href = redirectUrl;
        return;
      }
      
      showToast({
        message: error.message || 'Failed to fetch payment update link. Please try again.',
        severity: 'error',
      });
    }
  }, [showToast]);

  
  // ========== Subscription Expiration ==========
  // Reads from Redux: planDetails.subscriptionInfo.planEndDate, planDetails.name
  const subscriptionExpiration = useSubscriptionExpiration();

  // ========== Credit Exhaustion Modal Logic ==========
  const handleCloseCreditExhaustionModal = useCallback(() => {
    setCreditExhaustionModalOpen(false);
    // Reset the ref so modal can show again when billing data changes
    hasShownModalRef.current = false;
  }, []);

  // ========== Calculate Next Reset Date ==========
  // Get reset date from planDetails.subscriptionInfo.planEndDate
  const nextResetDate = useMemo(() => {
    if (planDetails?.subscriptionInfo?.planEndDate) {
      const endDate = new Date(planDetails.subscriptionInfo.planEndDate);
      return endDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    }
    
    // Fallback: Credits reset on the first day of each month
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return nextMonth.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }, [planDetails?.subscriptionInfo?.planEndDate]);

  // ========== Billing Cycle Data from subscriptionInfo ==========
  const billingCycle = useMemo(() => {
    // Only show billing cycle for Smart plan users
    if (!hasUpgradedPlan || !planDetails?.subscriptionInfo) {
      return null;
    }

    const subscriptionInfo = planDetails.subscriptionInfo;
    const nextPlanName = subscriptionInfo?.nextPeriodicPlan?.planName || 'Free';
    
    // Get plan price (Smart plan is $19/month)
    const planPrice = nextPlanName === 'Smart' ? 19 : 0;
    
    // Format next billing date from planEndDate
    let nextBillingDate = 'N/A';
    if (subscriptionInfo.planEndDate) {
      const endDate = new Date(subscriptionInfo.planEndDate);
      nextBillingDate = endDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    }

    // Format next charge amount
    const nextCharge = `$${planPrice.toFixed(2)}`;

    return {
      billingPeriod: 'Monthly', // Smart plan is monthly
      nextBillingDate,
      nextCharge,
    };
  }, [hasUpgradedPlan, planDetails]);

  // ========== Billing Data from planDetails ==========
  // Read actual data from planDetails (same pattern as useCreditLimitBanner)
  const billingData = useMemo(() => {
    // Get plan name from planDetails
    const planName = planDetails?.name || 'Free';
    
    // Determine plan price based on plan name
    const planPrice = planName === 'Free' ? 0 : planName === 'Smart' ? 19 : 0;
    
    // Read credit data from planDetails.credits array
    // Credits structure: [{ type: "base", balance: 8, limit: 8 }, { type: "prototype", balance: 1, limit: 1 }]
    // Note: balance = remaining credits, limit = total monthly credits
    let monthlyCredits = 0;
    let creditBalance = {
      name: 'Available Credits',
      used: 0,
      total: 0,
      percentage: 0,
    };

    if (planDetails?.credits && Array.isArray(planDetails.credits)) {
      // Find the base credit type (primary credits)
      const baseCredit = planDetails.credits.find((credit: any) => credit.type === 'base');
      if (baseCredit) {
        const limit = typeof baseCredit.limit === 'string' ? parseInt(baseCredit.limit, 10) : (baseCredit.limit || 0);
        const balance = typeof baseCredit.balance === 'string' ? parseInt(baseCredit.balance, 10) : (baseCredit.balance || 0);
        
        monthlyCredits = limit;
        const used = Math.max(0, limit - balance); // used = total - remaining
        // Percentage based on remaining credits (balance) not used credits
        // 8/8 remaining = 100%, 7/8 remaining = 87.5%, 6/8 remaining = 75%
        const percentage = limit > 0 ? (balance / limit) * 100 : 0;
        
        creditBalance = {
          name: 'Available Credits',
          used,
          total: limit,
          percentage: Math.min(100, Math.max(0, percentage)), // Clamp between 0-100
        };
      }
    }

    return {
      currentPlan: {
        name: planName,
        price: planPrice,
        billingPeriod: 'month',
      },
      creditBalances: [creditBalance],
      monthlyCredits,
      purchasedCredits: 0, // TODO: Get from API when available
      nextResetDate,
      availablePlans: availablePlans.length > 0 ? availablePlans : getDefaultPlans(planName),
      // Invoices from API (fetched separately, only for Smart plan users)
      invoices,
    };
  }, [planDetails, nextResetDate, hasUpgradedPlan, availablePlans, getDefaultPlans, invoices]);

  // ========== Credit Exhaustion Modal Trigger Logic ==========
  // Read base credits from planDetails (same pattern as useCreditLimitBanner)
  // Credits structure: [{ type: "base", balance: 8, limit: 8 }, { type: "prototype", balance: 1, limit: 1 }]
  // Note: balance = remaining credits, limit = total monthly credits
  const baseCreditData = useMemo(() => {
    if (planDetails?.credits && Array.isArray(planDetails.credits)) {
      // Find the base credit type
      const baseCredit = planDetails.credits.find((credit: any) => credit.type === 'base');
      if (baseCredit) {
        const limit = typeof baseCredit.limit === 'string' ? parseInt(baseCredit.limit, 10) : (baseCredit.limit || 0);
        const balance = typeof baseCredit.balance === 'string' ? parseInt(baseCredit.balance, 10) : (baseCredit.balance || 0);
        return {
          balance, // remaining credits
          limit,   // total monthly credits
        };
      }
    }
    return { balance: 0, limit: 0 };
  }, [planDetails]);

  // isFreePlan derived from currentPlanName (defined earlier in the hook)
  const isFreePlan = currentPlanName === 'Free';

  // Check if modal should be shown when billing page loads:
  // 1. User is on Free plan
  // 2. Base credits are exhausted (balance === 0)
  useEffect(() => {
    const isExhausted = baseCreditData.balance === 0;

    // Show modal if conditions are met and we haven't shown it yet for this billing data
    // Only trigger when planDetails or baseCreditData changes, not when modal state changes
    if (isFreePlan && isExhausted && !hasShownModalRef.current) {
      setCreditExhaustionModalOpen(true);
      hasShownModalRef.current = true;
    }
  }, [isFreePlan, baseCreditData.balance, baseCreditData.limit]);

  // ========== Handle Payment Success ==========
  // When user returns from Stripe with payment=success, show toast and refresh user data
  const paymentSuccessHandledRef = useRef(false);
  
  useEffect(() => {
    // Only handle payment success once
    if (paymentSuccess && !paymentSuccessHandledRef.current) {
      paymentSuccessHandledRef.current = true;
      
      // Show success toast
      showToast({
        message: 'Upgrade to Smart plan has been completed successfully.',
        severity: 'success',
      });
      
      // Refresh user profile to get updated plan details
      if (onRefreshUserProfile) {
        onRefreshUserProfile().catch((error) => {
          console.error('Failed to refresh user profile after payment:', error);
        });
      }
      
      // Update local state to show upgraded UI immediately
      setLocalUpgradeFlag(true);
    }
  }, [paymentSuccess, showToast, onRefreshUserProfile]);

  // Get credit values for modal (from base credits)
  const availableCredits = baseCreditData.balance;
  const totalCredits = baseCreditData.limit;

  // ========== Compute Pending Downgrade from API Data ==========
  // Check if there's a pending downgrade by looking at subscriptionInfo.nextPeriodicPlan
  const pendingDowngrade = useMemo(() => {
    const subscriptionInfo = planDetails?.subscriptionInfo;
    if (!subscriptionInfo?.nextPeriodicPlan) {
      return {
        isPending: false,
        effectiveDate: null,
        isCancelling: isCancellingDowngrade,
      };
    }

    // If current plan is Smart and nextPeriodicPlan.planName is "Free", there's a pending downgrade
    const isPending = currentPlanName === 'Smart' && subscriptionInfo.nextPeriodicPlan.planName === 'Free';
    
    // Get effective date from planEndDate
    let effectiveDate: string | null = null;
    if (isPending && subscriptionInfo.planEndDate) {
      const endDate = new Date(subscriptionInfo.planEndDate);
      effectiveDate = endDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    }

    return {
      isPending,
      effectiveDate,
      isCancelling: isCancellingDowngrade,
    };
  }, [planDetails, isCancellingDowngrade, currentPlanName]);

  // ========== Return Composed Facade ==========
  return useMemo(
    () => ({
      state: {
        isLoggedIn: headerConfig.isLoggedIn,
        user,
        hasUpgradedPlan,
      },
      header: headerConfig,
      navigation,
      auth,
      sidebar,
      actions: {
        onUpgrade: handleUpgrade,
        onConfirmUpgrade: handleConfirmUpgrade,
        onCancelUpgrade: handleCancelUpgrade,
        onDowngrade: handleDowngrade,
        onConfirmDowngrade: handleConfirmDowngrade,
        onCancelDowngrade: handleCancelDowngrade,
        onCancelPendingDowngrade: handleCancelPendingDowngrade,
        onConfirmCancelPendingDowngrade: handleConfirmCancelPendingDowngrade,
        onCloseCancelPendingDowngradeModal: handleCloseCancelPendingDowngradeModal,
        onRenewSubscription: handleRenewSubscription,
        onDownloadInvoice: handleDownloadInvoice,
        onUpdatePaymentMethod: handleUpdatePaymentMethod,
      },
      upgradeDialog: {
        isOpen: upgradeDialogOpen,
        isLoading: isUpgrading,
      },
      downgradeDialog: {
        isOpen: downgradeDialogOpen,
        isLoading: isDowngrading,
      },
      pendingDowngrade,
      cancelPendingDowngradeModal: {
        isOpen: cancelPendingDowngradeModalOpen,
        isLoading: isCancellingDowngrade,
      },
      creditExhaustionModal: {
        isOpen: creditExhaustionModalOpen,
        onClose: handleCloseCreditExhaustionModal,
        availableCredits,
        totalCredits,
      },
      subscriptionExpiration,
      data: {
        ...billingData,
        isLoadingPlans,
        isLoadingInvoices,
        paymentMethod,
        isLoadingPaymentMethod,
        billingCycle,
      },
    }),
    [headerConfig, navigation, auth, sidebar, handleUpgrade, handleConfirmUpgrade, handleCancelUpgrade, handleDowngrade, handleConfirmDowngrade, handleCancelDowngrade, handleCancelPendingDowngrade, handleConfirmCancelPendingDowngrade, handleCloseCancelPendingDowngradeModal, handleRenewSubscription, handleDownloadInvoice, handleUpdatePaymentMethod, billingData, user, upgradeDialogOpen, isUpgrading, downgradeDialogOpen, isDowngrading, pendingDowngrade, cancelPendingDowngradeModalOpen, isCancellingDowngrade, hasUpgradedPlan, subscriptionExpiration, creditExhaustionModalOpen, handleCloseCreditExhaustionModal, availableCredits, totalCredits, isLoadingPlans, isLoadingInvoices, invoices, paymentMethod, isLoadingPaymentMethod, billingCycle]
  );
}
